import { useTranslation } from 'next-i18next';
import React, { FC, useState, useRef, useEffect } from 'react';

import { Critic } from '@/types';
import Divider from '@/components/common/Divider';

import styles from './ProductNotation.module.scss';
import Icon from '@/components/common/iconedLinks/Icon';
import { URLS } from '@/constants/urls';

type ProductNotationProps = {
  ratings: Array<Critic>;
}

const ProductNotation: FC<ProductNotationProps> = ({ ratings }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      setIsOverflowing(containerRef.current.offsetHeight >= 400);
    }
  }, [ratings]);

  if (ratings.every((rating) => !rating.criticText)) {
    return null;
  }

  const isValid = (value: string) => /^[a-zA-Z0-9/]+$/.test(value);
  const finalRatings = ratings.filter((rating) => rating.criticText && rating.note && isValid(rating.note));

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>{t('product.notation')}</div>

      <div
        ref={containerRef}
        className={`${styles['notation-container']} ${expanded ? styles.expanded : ''}`}
      >
        {finalRatings.map((rating) => {
          const [note, ratingMax] = rating.note.split('/');
          return (
            <section key={rating.critic} className={styles['rating-container']}>
              <div className={styles.rating}>
                <p className={styles.value}>{note}</p>
                <p className={styles.max}>/{ratingMax}</p>
              </div>
              <Divider orientation="vertical" spacing={24} />
              <div className={styles['tasting-note']}>
                <span className={styles['critic']}>
                  <p className={styles['critic-diary']}>{rating.critic}</p>
                  <p className={styles['critic-author']}>{rating.author}</p>
                </span>
                <p className={styles['note']}>{rating.criticText}</p>
              </div>
            </section>
          );
        })}
        {isOverflowing && !expanded && <div className={styles.fadeOverlay} />}
      </div>

      {isOverflowing && !expanded && (
        <Icon
          className={styles.expand} src={URLS.ANGLE_BOTTOM_BLACK}
          width={32} height={32} alt="see more" onClick={() => setExpanded(!expanded)}
        />
      )}
    </div>
  );
};

export default ProductNotation;
